import React from 'react';
import SeeAllInclusionsLink from '../Components/Common/SeeAllInclusionsLink';
import { useSubscriptions } from '../Hooks/useSubscription';
import { useInvoiceInfo } from '../Hooks/useInvoiceInfo';
import { HorizontalDivider } from '../Components/Common/HorizontalDivider';
import { Title } from '../Components/Common/Title';
import { InvoiceInformationPanel } from '../Components/Summary/InvoiceInformationPanel';
import { CreditCardPanel } from '../Components/Summary/CreditCardPanel';
import { useCreditCard } from '../Hooks/useCreditCard';
import { InvoiceListPanel } from '../Components/Summary/InvoiceListPanel';
import { useHistory } from 'react-router';
import { AnimatedDiv } from '@vimily/bonjoro-ui-components-v2/AnimatedDiv';
import { ProductSummary } from '../Components/Summary/ProductSummary';
import useCurrentTeam from '~/react-hooks/use-current-team';
import usePlans from '~/react-hooks/use-plans';
import { UpcomingBill } from '../Components/Summary/UpcomingBill';
import { useFeatures } from '~/react-hooks/use-features';
import { FallbackProductSummary } from '../Components/Summary/FallbackProductSummary';
import { useFallbackUsage } from '~/react-hooks/use-fallback-usage';
import useDate from '~/react-hooks/use-date';

export function Summary() {
  const { data: subscriptions } = useSubscriptions({});
  const { data: invoiceInfo } = useInvoiceInfo({});
  const { data: creditCard } = useCreditCard({});
  const { card_on_file } = useCurrentTeam();
  const { data: fallbackUsage } = useFallbackUsage();
  const { acceptsPaymentMethod } = useFeatures();
  const plans = usePlans();
  const hasMultiplePlans = plans.length > 1;
  const history = useHistory();
  const { local } = useDate(fallbackUsage?.period_end_at);

  const fallbackBillingDate = local?.toISODate();
  const invoiceBillingDate = invoiceInfo?.upcoming_invoice?.date;
  const showFallbackBillingDate = fallbackBillingDate !== invoiceBillingDate;
  const fallbackUsageFee = fallbackUsage?.fees?.[0];

  if (!subscriptions) return <></>;
  return (
    <AnimatedDiv show className='flex flex-col gap-y-5 max-w-[1200px]'>
      <div className='flex flex-row justify-between mb-2 w-full'>
        <Title label='Billing summary' />
        <SeeAllInclusionsLink
          trackingContext='billing summary'
          service={
            !hasMultiplePlans && plans[0].service ? plans[0].service : undefined
          }
        />
      </div>
      {subscriptions?.map((subscription, key) => (
        <ProductSummary subscription={subscription} key={key} />
      ))}
      {invoiceInfo != undefined &&
        fallbackUsageFee?.usage != undefined &&
        fallbackUsageFee?.usage > 0 && (
          <FallbackProductSummary
            usage={fallbackUsageFee?.usage}
            price={fallbackUsageFee?.amount}
            billingDate={
              showFallbackBillingDate ? fallbackBillingDate : undefined
            }
          />
        )}
      {hasMultiplePlans && (
        <UpcomingBill
          billingDate={invoiceInfo?.upcoming_invoice?.date}
          upcomingBill={invoiceInfo?.upcoming_invoice?.total}
        />
      )}
      <HorizontalDivider />
      {acceptsPaymentMethod && (
        <>
          <Title className='py-2' label='Billing information' />
          <CreditCardPanel
            creditCard={creditCard}
            hasSavedCreditCard={card_on_file}
            boxClassName='!flex-col xmd:!flex-row'
            detailContainerClassName='!flex-col xmd:!flex-row'
            onClick={() => history.push('/billing/cards/create')}
          />
          <InvoiceInformationPanel
            invoiceProfile={invoiceInfo?.billing_profile}
          />
          <HorizontalDivider />
        </>
      )}
      <InvoiceListPanel
        upcomingInvoice={invoiceInfo?.upcoming_invoice}
        invoices={invoiceInfo?.invoices}
      />
    </AnimatedDiv>
  );
}
